import * as React from 'react'
import { useState, useEffect, useCallback, useRef } from 'react'

import moment from 'moment'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

import {
  BusinessTripRate,
  BusinessTripRatesConfigurationModel,
} from 'app/models/configuration/businessTripRatesConfigurationModel'
import { CountryModel } from 'app/models/timesheet/countryModel'
import { MessageModel } from 'app/models/messageModel'

import { MessageType } from 'app/constants/messageType'
import { CurrencyModel } from 'app/models/newDelegation/currencyModel'

import { BusinessTripRatesRestoreOption } from 'app/constants/configuration/businessTripRatesRestoreOption'
import { faBed, faHotel, faPlus, faForkKnife } from '@/lib/fa'
import { Checkbox } from '@/ui'
import { ContentHeaderBar } from '@/modules/settings/components/ContentHeaderBar'

import {
  //   isRightDates,
  emptyValidation,
  runValidation,
} from './utils'
import { BusinessTripRatesTable } from './ui/BusinessTripRatesTable'
import { useWrapConfirm } from '@/modules/shared/hooks/useWrapConfirm'
import { BusinessTripRatesTableProvider } from './context/BusinessTripRatesTableContext'
import { SettingsPageWrapper } from '../../components/SettingsPageWrapper'

export type BusinessTripRatesConfigurationStateProps = {
  companyId: string
  configuration: BusinessTripRatesConfigurationModel
  countries: Array<CountryModel>
  currencies: Array<CurrencyModel>
}

export type BusinessTripRatesConfigurationDispatchProps = {
  showMessage: (message: MessageModel) => void
  getCountries: (companyId: string) => Promise<Array<CountryModel>>
  getCurrencies: (companyId: string) => Promise<Array<CurrencyModel>>
  getBusinessTripRatesConfiguration: (
    companyId: string,
  ) => Promise<ReadonlyArray<BusinessTripRatesConfigurationModel>>
  saveBusinessTripRatesConfiguration: (
    companyId: string,
    configuration: BusinessTripRatesConfigurationModel,
  ) => Promise<BusinessTripRatesConfigurationModel>
  restoreBusinessTripRatesConfiguration: (
    companyId: string,
    restoreOption: BusinessTripRatesRestoreOption,
  ) => Promise<BusinessTripRatesConfigurationModel>
}

export type BusinessTripRatesConfigurationOwnProps = {
  companyId: string
}

type BusinessTripRatesConfigurationProps =
  BusinessTripRatesConfigurationStateProps &
    BusinessTripRatesConfigurationDispatchProps &
    BusinessTripRatesConfigurationOwnProps

const BusinessTripRatesConfiguration: React.FC<
  BusinessTripRatesConfigurationProps
> = (props) => {
  const { t: translate } = useLocalize()
  const {
    companyId,
    configuration,
    countries,
    currencies,
    showMessage,
    getCountries,
    getCurrencies,
    getBusinessTripRatesConfiguration: getConfigProp,
    saveBusinessTripRatesConfiguration: saveConfigProp,
    restoreBusinessTripRatesConfiguration: restoreConfigProp,
  } = props

  const [showActive, setShowActive] = useState(true)
  const [showPriority, setShowPriority] = useState(true)
  const [rates, setRates] = useState<BusinessTripRate[]>([])
  const [homelandCountry, setHomelandCountry] = useState<CountryModel | null>(
    null,
  )
  const [homelandCurrency, setHomelandCurrency] =
    useState<CurrencyModel | null>(null)
  const [canSave, setCanSave] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [pending, setPending] = useState(false)
  const [saving, setSaving] = useState(false)

  // --- Effects ---

  const fetchConfiguration = useCallback(
    async (cId: string) => {
      setPending(true)
      await getConfigProp(cId || companyId)
      setPending(false)
    },
    [getConfigProp, companyId],
  )

  // Fetch on mount and when companyId changes
  useEffect(() => {
    if (companyId != null) {
      fetchConfiguration(companyId)
      getCountries(companyId)
      getCurrencies(companyId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId])

  // Sync configuration prop to local state
  useEffect(() => {
    if (configuration) {
      setRates(configuration.rates)
      setHomelandCountry(configuration.homelandCountry)
      setHomelandCurrency(configuration.homelandCurrency)
      setCanSave(false)
    }
  }, [configuration])

  // Compute validation (replicates ValidatorBase.render logic)
  const validation = submitted
    ? runValidation(rates, translate)
    : emptyValidation()

  // When validation becomes invalid, show all rows (replaces componentWillUpdate)
  const prevIsValid = useRef(true)
  useEffect(() => {
    if (validation.isValid !== prevIsValid.current) {
      if (!validation.isValid) {
        setShowActive(false)
        setShowPriority(false)
      }
      prevIsValid.current = validation.isValid
    }
  })

  // --- Helpers ---

  const updateRate = (rateId: number, updates: Partial<BusinessTripRate>) => {
    setRates((prev) =>
      prev.map((rate) => (rate.id === rateId ? { ...rate, ...updates } : rate)),
    )
    setCanSave(true)
  }

  const deleteRate = (rateId: number) => {
    setRates((prev) => prev.filter((rate) => rate.id !== rateId))
    setCanSave(true)
  }

  const filter = () => {
    let rows = rates || []
    if (showActive) {
      rows = rows.filter((r) => (r.isActive || r.isNew) && !r.isDeleted)
    }
    if (showPriority) {
      rows = rows.filter(
        (r) => (r.isPriorityCountry || r.isNew) && !r.isDeleted,
      )
    }
    return rows
  }

  // --- Actions ---

  const saveData = async () => {
    setSaving(true)
    const config: BusinessTripRatesConfigurationModel = {
      rates: rates.map((r) => (r.isNew ? { ...r, id: 0 } : r)),
    }
    await saveConfigProp(companyId, config)

    showMessage({
      text: translate('configuration.save-changes-title'),
      details: translate('configuration.save-changes-details'),
      type: MessageType.INFO,
    })

    setCanSave(false)
    setSaving(false)
    fetchConfiguration(companyId)
  }

  const handleFormSubmit = () => {
    const v = runValidation(rates, translate)
    setSubmitted(true)
    if (v.isValid) {
      saveData()
    }
  }

  const restoreConfiguration = async (
    option: BusinessTripRatesRestoreOption,
  ) => {
    await restoreConfigProp(companyId, option)

    setCanSave(true)
    showMessage({
      text: translate(
        `configuration.businessTripRates.restoreDefault${BusinessTripRatesRestoreOption[option]}Title`,
      ),
      details: translate(
        `configuration.businessTripRates.restoreDefault${BusinessTripRatesRestoreOption[option]}Details`,
      ),
      type: MessageType.INFO,
    })
  }

  const handleCancel = () => {
    setCanSave(false)
    setRates(configuration.rates)
    setSubmitted(false)
    fetchConfiguration(companyId)
    showMessage({
      text: translate('configuration.cancel-changes-title'),
      details: translate('configuration.cancel-changes-details'),
      type: MessageType.WARNING,
    })
  }

  const addNewRate = () => {
    const newRates = [
      {
        isNew: true,
        isEdited: true,
        country: homelandCountry,
        allowanceCurrency: homelandCurrency,
        allowanceAccomodationCurrency: homelandCurrency,
        allowanceHotelLimitationCurrency: homelandCurrency,
        allowanceSanitarityCurrency: homelandCurrency,
        id: Math.max(...rates.map((r) => r.id)) + 1,
        dateFrom: moment(),
        allowanceRate: 0,
        allowanceSanitarityRate: 0,
        allowanceAccomodationRate: 0,
        allowanceHotelLimitationRate: 0,
      },
      ...rates,
    ]
    setCanSave(true)
    setRates(newRates as any)
  }

  // --- Render Helpers ---





  const wrapConfirm = useWrapConfirm()
  const wrappedDelete = wrapConfirm(deleteRate, {
    message: 'Czy na pewno chcesz usunąć tą stawkę?',
  })

  // --- Render ---

  if (rates == null) {
    return null
  }

  const filteredRates = filter()

  return (
    <SettingsPageWrapper
      isDirty={canSave}
      isReady={Boolean(rates)}
      isPending={saving}
      // isError={!!q.isError}
      // errorMsg={q.isError ? t('configuration-businessTrip.load-error') : ''}
      handleSave={validation.isValid && handleFormSubmit}
      handleReset={handleCancel}
      // isValid={validation.isValid}
      // globalErrorMsg={t('configuration-businessTrip.load-error-global')}
      header={
        <>
          <ContentHeaderBar
            title={translate('configuration-menu.business-trip-rates')}
            actions={[
              {
                icon: faPlus,
                title: translate('configuration.businessTripRates.addNew'),
                onClick: addNewRate,
              },
              {
                icon: faForkKnife,
                title: translate(
                  'configuration.businessTripRates.restoreDefaultAllowance',
                ),
                onClick: () =>
                  restoreConfiguration(
                    BusinessTripRatesRestoreOption.Allowance,
                  ),
              },
              {
                icon: faBed,
                title: translate(
                  'configuration.businessTripRates.restoreDefaultAccommodationRate',
                ),
                onClick: () =>
                  restoreConfiguration(
                    BusinessTripRatesRestoreOption.Accomodation,
                  ),
              },
              {
                icon: faHotel,
                title: translate(
                  'configuration.businessTripRates.restoreDefaultHotelLimit',
                ),
                onClick: () =>
                  restoreConfiguration(
                    BusinessTripRatesRestoreOption.HotelLimit,
                  ),
              },
            ]}
          >
            <div className="tw-flex tw-items-center tw-justify-center tw-gap-1">
              <Checkbox
                checked={showActive}
                onChange={() => setShowActive((prev) => !prev)}
                id="showActive"
              />
              <span>
                {translate('configuration.businessTripRates.onlyActive')}
              </span>
            </div>
            <div className="tw-flex tw-items-center tw-justify-center tw-gap-1">
              <Checkbox
                checked={showPriority}
                onChange={() => setShowPriority((prev) => !prev)}
                id="showPriority"
              />
              <span>
                {translate('configuration.businessTripRates.onlyOftenUsed')}
              </span>
            </div>
          </ContentHeaderBar>
        </>
      }
    >
      <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col">
        <BusinessTripRatesTableProvider
          rates={filteredRates}
          savedRates={configuration ? configuration.rates : []}
          availableCountries={countries || []}
          availableCurrencies={currencies || []}
          handleUpdate={updateRate}
          handleDelete={wrappedDelete}
        >
          <BusinessTripRatesTable />
        </BusinessTripRatesTableProvider>


        {/* {canSave && (
          <LegacyBottomActionsBar
            className="tw-sticky tw-bottom-0"
            isValid={false} //={validation.isValid}
            isPending={saving}
            handleSave={handleFormSubmit}
            handleCancel={handleCancel}
          />
        )} */}
      </div>
    </SettingsPageWrapper>
  )
}

export default BusinessTripRatesConfiguration
